/**
 * "Build a Kit" API client.
 *
 * Unlike the other files in this folder this is a plain module rather than a
 * Vue mixin, because it is called from the `kitBuilder` Vuex module (actions
 * have no component `this`). It follows the same conventions as the mixins:
 * `process.env.MIX_API_ENDPOINT` as the base and the JWT from the stored
 * session as a Bearer header.
 */
import axios from "axios";
import FileSaver from "file-saver";

const route = "kit-builder";

function baseUrl() {
    return process.env.MIX_API_ENDPOINT + `/${route}`;
}

function authorizationToken() {
    const session = localStorage.getItem(process.env.MIX_USER_SESSION_VARIABLE_NAME);
    return session ? JSON.parse(session).token.access_token : "";
}

function headers() {
    return { Authorization: `Bearer ${authorizationToken()}` };
}

/**
 * Single-resource endpoints return Laravel's API-resource envelope
 * (`{ "data": { ... } }`); paginated collections keep their own envelope and
 * are handled by listProjects() below.
 */
function unwrap(response) {
    const body = response.data;
    return body && Object.prototype.hasOwnProperty.call(body, "data") ? body.data : body;
}

function filenameFromResponse(response, fallback) {
    const disposition = response.headers && response.headers["content-disposition"];
    if (!disposition) return fallback;
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    return match ? decodeURIComponent(match[1]) : fallback;
}

function download(path, fallbackName) {
    return axios({
        method: "get",
        url: `${baseUrl()}${path}`,
        headers: headers(),
        responseType: "blob",
    }).then((response) => {
        FileSaver.saveAs(response.data, filenameFromResponse(response, fallbackName));
        return response;
    });
}

export default {
    getCatalog() {
        return axios({
            method: "get",
            url: `${baseUrl()}/catalog`,
            headers: headers(),
        }).then((r) => r.data);
    },

    /** Returns the paginated envelope: `{ data: [...], links, meta }`. */
    listProjects(params = {}) {
        return axios({
            method: "get",
            url: `${baseUrl()}/projects`,
            headers: headers(),
            params: params,
        }).then((r) => r.data);
    },

    createProject(payload) {
        return axios({
            method: "post",
            url: `${baseUrl()}/projects`,
            headers: headers(),
            data: payload,
        }).then(unwrap);
    },

    getProject(projectId) {
        return axios({
            method: "get",
            url: `${baseUrl()}/projects/${projectId}`,
            headers: headers(),
        }).then(unwrap);
    },

    updateProject(projectId, payload) {
        return axios({
            method: "patch",
            url: `${baseUrl()}/projects/${projectId}`,
            headers: headers(),
            data: payload,
        }).then(unwrap);
    },

    deleteProject(projectId) {
        return axios({
            method: "delete",
            url: `${baseUrl()}/projects/${projectId}`,
            headers: headers(),
        });
    },

    addItem(projectId, payload) {
        return axios({
            method: "post",
            url: `${baseUrl()}/projects/${projectId}/items`,
            headers: headers(),
            data: payload,
        }).then(unwrap);
    },

    updateItem(projectId, itemId, payload) {
        return axios({
            method: "patch",
            url: `${baseUrl()}/projects/${projectId}/items/${itemId}`,
            headers: headers(),
            data: payload,
        }).then(unwrap);
    },

    removeItem(projectId, itemId) {
        return axios({
            method: "delete",
            url: `${baseUrl()}/projects/${projectId}/items/${itemId}`,
            headers: headers(),
        });
    },

    /**
     * Exports are fetched as blobs rather than opened as plain links so the
     * JWT header still goes with the request (the export routes sit behind
     * the same `jwt.verify` middleware as everything else).
     */
    downloadPdf(projectId, fallbackName = "kit-quote.pdf") {
        return download(`/projects/${projectId}/export/pdf`, fallbackName);
    },

    downloadCsv(projectId, fallbackName = "kit-quote.csv") {
        return download(`/projects/${projectId}/export/csv`, fallbackName);
    },

    /* ---------------------------------------------------------------------
     * Submitted kit orders. These endpoints return the portal's standard
     * { success, message, data } envelope rather than an API-resource one.
     * ------------------------------------------------------------------ */

    submitOrder(payload) {
        return axios({
            method: "post",
            url: `${baseUrl()}/orders`,
            headers: headers(),
            data: payload,
        }).then((r) => r.data);
    },

    listOrders(params = {}) {
        return axios({
            method: "get",
            url: `${baseUrl()}/orders`,
            headers: headers(),
            params: params,
        }).then((r) => r.data);
    },

    getOrder(orderCode) {
        return axios({
            method: "get",
            url: `${baseUrl()}/orders/${encodeURIComponent(orderCode)}`,
            headers: headers(),
        }).then((r) => r.data);
    },

    downloadOrderPdf(orderCode) {
        return download(`/orders/${encodeURIComponent(orderCode)}/pdf`, `${orderCode}.pdf`);
    },

    downloadOrderCsv(orderCode) {
        return download(`/orders/${encodeURIComponent(orderCode)}/csv`, `${orderCode}.csv`);
    },
};
